import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { relativeTime } from '../feedback-presentation/feedback-presentation';

/** One comment in the thread. Presentational — the host maps its domain object onto it. */
export interface CommentThreadEntry {
  id: string;
  /** Display name of the author. */
  author?: string;
  /**
   * The author's address. Not rendered — it decides whether a comment is the viewer's
   * own (shown on the right; see {@link CommentThread.currentUserEmail}) and lets the
   * composer default the Notify picker to whoever spoke last, so supply it wherever
   * the host has it.
   */
  authorEmail?: string | null;
  /** Role label shown after the name ("UX Lead", "Client"). */
  role?: string | null;
  /**
   * Which side of the conversation this came from. Only used when the host passes no
   * {@link CommentThread.currentUserEmail}: then team comments align right in the
   * accent color and client comments left. With an email, the viewer's own comments
   * are the ones on the right.
   */
  side: 'team' | 'client';
  text: string;
  /** ISO date string. */
  created?: string;
  /** Internal-only comment: shown with a PRIVATE badge and a dashed outline. */
  isPrivate?: boolean;
}

/** Someone who can be emailed about a new comment. */
export interface CommentRecipientOption {
  /** The address the API notifies. Also the option's identity. */
  email: string;
  /** Display name; falls back to the email when absent. */
  label?: string;
}

/** What the composer emits when someone sends a comment. */
export interface NewComment {
  comment: string;
  isPrivate: boolean;
  /**
   * Who to email. Empty when the host offers no recipient picker (the widget), in
   * which case the API falls back to notifying the feedback's own author.
   */
  recipients: string[];
  /**
   * False when the picker is offered and nobody is chosen: the comment is a note
   * (e.g. one left for yourself) and nobody is emailed. True otherwise.
   */
  notify: boolean;
}

/**
 * A comment's created time in milliseconds, or null when it has none or it can't be
 * read. The API sends six fractional digits (`.300000Z`); the standard date format
 * only promises three, so the extra digits are trimmed before parsing.
 */
function commentTime(created: string | undefined): number | null {
  if (!created) {
    return null;
  }
  const parsedTime = Date.parse(created.replace(/(\.\d{3})\d+/, '$1'));

  return Number.isNaN(parsedTime) ? null : parsedTime;
}

/**
 * The comment thread on a feedback item: a composer, then the conversation newest
 * first (the order the design shows, so a new comment lands directly under the box
 * you typed it in).
 *
 * Presentational: the host supplies `comments` and handles `post` — including the API
 * call and any refresh.
 */
@Component({
  selector: 'fb-comment-thread',
  templateUrl: './comment-thread.html',
  styleUrl: './comment-thread.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Closes the recipient dropdown on a press anywhere outside it, or on Escape.
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
    '(document:pointerup)': 'onDocumentPointerUp()',
    '(document:keydown.escape)': 'closeRecipients()',
  },
})
export class CommentThread {
  private readonly hostElement = inject(ElementRef<HTMLElement>);

  /** The thread, in any order — the component sorts newest first for display. */
  readonly comments = input<CommentThreadEntry[]>([]);

  /**
   * Whether this viewer may mark a comment internal. False hides the toggle entirely
   * AND forces every comment public: the API refuses private comments from
   * client-scoped callers, so offering the control — or defaulting to private — there
   * would only produce a rejection.
   */
  readonly canPostPrivate = input(false);

  /**
   * Everyone who can be emailed about a comment — in the management app, the people
   * who have submitted feedback on this round.
   *
   * Leave empty (the default, and what the on-page widget does) to hide the picker
   * entirely; the API then notifies the feedback's author as it always has. Supplying
   * options makes choosing at least one REQUIRED before the comment can be sent.
   */
  readonly recipientOptions = input<CommentRecipientOption[]>([]);

  /**
   * The author of the feedback item itself. Their option is badged in the list, and
   * they are the default recipient until somebody comments — see
   * {@link defaultRecipients}.
   */
  readonly feedbackAuthorEmail = input<string | null>(null);

  /**
   * The signed-in user's address, so the default recipient is never themselves:
   * "notify whoever spoke last" should skip your own comments, or replying twice in a
   * row would pre-select you.
   *
   * Also what keeps the picker stable while you work — because your own comment does
   * not change the default, posting one leaves your chosen recipients alone.
   */
  readonly currentUserEmail = input<string | null>(null);

  /**
   * The email domain a default recipient must belong to, without the '@'.
   *
   * Replies are defaulted to the team rather than to the client, so only a commenter
   * on this domain is picked up automatically; anyone else has to be chosen by hand.
   * Set to an empty string to consider the last commenter whatever their domain.
   */
  readonly preferredNotifyDomain = input('brafton.com');

  /**
   * The team's own email domain, without the '@' (subdomains count). Picking anyone
   * outside it forces the comment public — the email carries the comment, so it can't
   * stay internal — and the API applies the same rule.
   */
  readonly internalEmailDomain = input('brafton.com');

  /** True while a comment is being sent; disables the composer. */
  readonly posting = input(false);

  /** True while the thread is being fetched. */
  readonly loading = input(false);

  /** Emitted when the user sends a comment. */
  readonly post = output<NewComment>();

  /** Composer text. */
  protected readonly draft = signal('');

  /**
   * Whether the pending comment is marked internal. Private by default: a comment is
   * an internal note until someone chooses to publish it to the client.
   */
  protected readonly draftPrivate = signal(true);

  /** Whether the recipient dropdown is expanded. */
  protected readonly recipientsOpen = signal(false);

  /** A pointer press that started inside the dropdown is in progress. */
  private pressInsideRecipients = false;

  /** Whether to show the picker at all — driven purely by the host supplying options. */
  protected readonly showRecipients = computed(() => this.recipientOptions().length > 0);

  /**
   * Who a reply is aimed at unless the user says otherwise.
   *
   * A comment thread is a conversation, so the default is whoever spoke last —
   * specifically the most recent commenter who is on {@link preferredNotifyDomain} and
   * is not the current user, walking the thread newest-first. Before anyone has
   * commented (or when nobody in the thread qualifies) it falls back to the author of
   * the feedback item, who is the person the first reply is for.
   *
   * Only addresses actually offered in {@link recipientOptions} are used: a
   * pre-selection the list cannot show is one the user has no way to undo.
   *
   * Ends up empty when the only candidate is the current user, so a comment isn't
   * emailed back to yourself — leaving nobody chosen sends it as a note.
   *
   * The value-based `equal` matters: without it every thread refetch would hand
   * {@link selectedRecipients} a fresh array and silently reset a hand-picked choice.
   */
  protected readonly defaultRecipients = computed<string[]>(
    () => {
      const options = this.recipientOptions();
      if (!options.length) {
        return [];
      }

      const currentUser = this.currentUserEmail();
      const isSelf = (email: string) => !!currentUser && this.sameEmail(currentUser, email);
      const isOffered = (email: string) =>
        options.some((option) => this.sameEmail(option.email, email));

      const domain = this.preferredNotifyDomain().trim().toLowerCase().replace(/^@/, '');
      const onPreferredDomain = (email: string) =>
        !domain || email.trim().toLowerCase().endsWith(`@${domain}`);

      for (const entry of this.ordered()) {
        const email = entry.authorEmail;
        if (
          email &&
          !isSelf(email) &&
          isOffered(email) &&
          onPreferredDomain(email)
        ) {
          return [email];
        }
      }

      const author = this.feedbackAuthorEmail();

      return author && !isSelf(author) && isOffered(author) ? [author] : [];
    },
    {
      equal: (first, second) =>
        first.length === second.length &&
        first.every((email, index) => this.sameEmail(email, second[index])),
    },
  );

  /**
   * The chosen recipients, seeded from {@link defaultRecipients}. linkedSignal (not an
   * effect) so that swapping to a different feedback item — or the thread finally
   * loading — re-seeds for the new conversation instead of carrying the previous
   * item's choice over, while a hand-picked selection survives everything that leaves
   * the default alone.
   */
  protected readonly selectedRecipients = linkedSignal<string[], string[]>({
    source: () => this.defaultRecipients(),
    computation: (defaults) => [...defaults],
  });

  /**
   * The privacy actually sent. A host that cannot post privately (the widget) always
   * sends public, whatever the local toggle says — otherwise the default above would
   * make every client comment a 403.
   */
  protected readonly effectivePrivate = computed(
    () => this.canPostPrivate() && !this.notifiesExternal() && this.draftPrivate(),
  );

  /**
   * Whether anyone picked to be emailed is outside {@link internalEmailDomain}. While
   * true the comment is locked public; removing them restores the user's own choice.
   */
  protected readonly notifiesExternal = computed(() => {
    const domain = this.internalEmailDomain().trim().toLowerCase().replace(/^@/, '');

    return (
      this.showRecipients() &&
      this.selectedRecipients().some((email) => {
        const emailDomain = email.trim().toLowerCase().split('@').pop() ?? '';

        return emailDomain !== domain && !emailDomain.endsWith(`.${domain}`);
      })
    );
  });

  protected readonly relativeTime = relativeTime;

  /**
   * Newest first, without mutating the input array. Comments with the same time keep
   * their reverse input order (the API sends oldest first), and undated ones sink to
   * the bottom, so the order never depends on how a browser breaks ties.
   */
  protected readonly ordered = computed(() =>
    this.comments()
      .map((entry, inputIndex) => ({ entry, inputIndex, createdTime: commentTime(entry.created) }))
      .sort(
        (first, second) =>
          (second.createdTime ?? Number.NEGATIVE_INFINITY) - (first.createdTime ?? Number.NEGATIVE_INFINITY) ||
          second.inputIndex - first.inputIndex,
      )
      .map(({ entry }) => entry),
  );

  /** Summary shown on the closed dropdown. */
  protected readonly recipientSummary = computed(() => {
    const selected = this.selectedRecipients();
    if (!selected.length) {
      return 'No one (just a note)';
    }
    if (selected.length === 1) {
      return this.labelFor(selected[0]);
    }

    return `${this.labelFor(selected[0])} +${selected.length - 1}`;
  });

  /** Whether Send is pressable: there is text, and nothing is already being sent. */
  protected readonly canSend = computed(() => this.draft().trim().length > 0 && !this.posting());

  protected send(): void {
    if (!this.canSend()) {
      return;
    }

    const chosenRecipients = this.showRecipients() ? [...this.selectedRecipients()] : [];
    this.post.emit({
      comment: this.draft().trim(),
      isPrivate: this.effectivePrivate(),
      // Empty when there is no picker, which the API reads as "notify the author".
      recipients: chosenRecipients,
      // Nobody picked from an offered list: a note, emailed to no one.
      notify: !this.showRecipients() || chosenRecipients.length > 0,
    });

    // Clear the text but keep the private choice and the recipients: someone working
    // through a review usually leaves several comments for the same people in a row.
    this.draft.set('');
    this.recipientsOpen.set(false);
  }

  protected togglePrivate(): void {
    this.draftPrivate.update((isPrivate) => !isPrivate);
  }

  protected toggleRecipients(): void {
    this.recipientsOpen.update((isOpen) => !isOpen);
  }

  protected isSelected(email: string): boolean {
    return this.selectedRecipients().some((selected) => this.sameEmail(selected, email));
  }

  /** Whether this option is the author of the feedback item being commented on. */
  protected isFeedbackAuthor(email: string): boolean {
    const author = this.feedbackAuthorEmail();

    return !!author && this.sameEmail(author, email);
  }

  protected toggleRecipient(email: string): void {
    this.selectedRecipients.update((selected) =>
      selected.some((chosen) => this.sameEmail(chosen, email))
        ? selected.filter((chosen) => !this.sameEmail(chosen, email))
        : [...selected, email],
    );
  }

  /**
   * A press anywhere but the dropdown itself closes it — including the rest of the
   * thread (the comment box, Send, the comments), not just outside the component.
   * Pointer-down rather than click, so it still closes when something else swallows
   * the click.
   */
  protected onDocumentPointerDown(event: Event): void {
    this.pressInsideRecipients = this.isInsideRecipientsField(event.target);
    if (this.recipientsOpen() && !this.pressInsideRecipients) {
      this.recipientsOpen.set(false);
    }
  }

  protected onDocumentPointerUp(): void {
    this.pressInsideRecipients = false;
  }

  /**
   * Keyboard focus leaving the dropdown (e.g. tabbing on to the comment box) closes it.
   * Not during a press inside it: Safari doesn't focus a clicked button, so picking a
   * name would look like focus leaving and close the menu before the pick lands.
   */
  protected onRecipientsFocusOut(event: FocusEvent): void {
    if (this.recipientsOpen() && !this.pressInsideRecipients && !this.isInsideRecipientsField(event.relatedTarget)) {
      this.recipientsOpen.set(false);
    }
  }

  protected closeRecipients(): void {
    this.recipientsOpen.set(false);
  }

  /** Whether an event target is the dropdown's trigger or its menu. */
  private isInsideRecipientsField(target: EventTarget | null): boolean {
    const recipientsField = this.hostElement.nativeElement.querySelector('.recipients-field');

    return !!recipientsField && target instanceof Node && recipientsField.contains(target);
  }

  /**
   * Whether a comment is the viewer's own, shown on the right: its author email is the
   * signed-in user's. Without a signed-in email, team comments take the right side.
   */
  protected isMine(entry: CommentThreadEntry): boolean {
    const viewerEmail = this.currentUserEmail();
    if (viewerEmail) {
      return !!entry.authorEmail && this.sameEmail(entry.authorEmail, viewerEmail);
    }

    return entry.side === 'team';
  }

  /** First initial for the avatar bubble, matching the design. */
  protected initial(name: string | undefined): string {
    return (name?.trim()[0] ?? '?').toUpperCase();
  }

  /** The display name for an address, falling back to the address itself. */
  private labelFor(email: string): string {
    const option = this.recipientOptions().find((candidate) => this.sameEmail(candidate.email, email));

    return option?.label?.trim() || email;
  }

  /** Addresses are case-insensitive, and the API de-dupes them the same way. */
  private sameEmail(first: string, second: string): boolean {
    return first.trim().toLowerCase() === second.trim().toLowerCase();
  }
}
